import { ActionContext } from "vuex";
import eventContentUseCase from "../../../../domain/usecase/content_provider/event/content_repository";
import {
  EventBasedOnClass,
  EventClass,
  EventCourse,
  EventRequest
} from "../../../../domain/entities/courseEvent/courseEvent";
import { InterfaceEventUseCases } from "../../../../domain/usecase/impl/event/eventUseCases";
import {
  AttendanceAt,
  Instructor,
  User
} from "../../../../domain/entities/entities";
import store from "../store";
import { Content } from "../../../presentation/views/content/helpers";
import { EventContentProvider } from "../../../../domain/entities/content-provider/event-content-provider";
import { dateGrouped } from "../../../presentation/components/mixins";

class EventState {
  eventsBasedOnClass: EventBasedOnClass[] = [];
  eventCourses: EventCourse[] = [];
  eventRequest: EventRequest = new EventRequest();
  event: EventCourse = new EventCourse();
  eventClass: EventClass = new EventClass();
  contents: Content[] = [];
  isUserEnrolled: boolean = false;
  isUserAttendance: boolean = false;
  instructors: Instructor[] = [];
  certificate = "";
  enrolledUser: User = new User();
  dateQrCode = "";
}

const state = new EventState();

const mutations = {
  SET_EVENT_BASED_ON_CLASSES(
    state: EventState,
    eventsBasedOnClass: EventBasedOnClass[]
  ): void {
    state.eventsBasedOnClass = eventsBasedOnClass;
  },
  SET_EVENT_REQUEST(state: EventState, eventCourse: EventRequest): void {
    state.eventRequest = eventCourse;
  },
  SET_EVENT(state: EventState, eventCourse: EventCourse): void {
    state.event = eventCourse;
  },
  SET_EVENTS(state: EventState, eventCourses: EventCourse[]): void {
    state.eventCourses = eventCourses;
  },
  SET_EVENT_CONTENTS(state: EventState, contents: Content[]): void {
    state.contents = contents;
  },
  SET_EVENT_CLASS(state: EventState, eventClass: EventClass): void {
    state.eventClass = eventClass;
  },
  ENROLL_USER(state: EventState, enrolledUser: User): void {
    state.isUserEnrolled = true;
    state.enrolledUser = enrolledUser;
  },
  UN_ENROLL_USER(state: EventState): void {
    state.isUserEnrolled = false;
  },
  ATTENDANCE_USER(state: EventState): void {
    state.isUserAttendance = true;
  },
  UN_ATTENDANCE_USER(state: EventState): void {
    state.isUserAttendance = false;
  },
  SET_INSTRUCTORS(state: EventState, instructors: Instructor[]): void {
    state.instructors = instructors;
  },
  SET_USER_CERTIFICATE(state: EventState, certificate: string): void {
    state.certificate = certificate;
  },
  SET_DATE_QR_CODE(state: EventState, date: string): void {
    state.dateQrCode = date;
  }
};

const actions = {
  async list({ commit }: ActionContext<EventState, EventState>): Promise<void> {
    const eventCourses: EventCourse[] = await InterfaceEventUseCases.list();

    commit("SET_EVENTS", eventCourses);
  },
  async getEventCourse(
    { commit }: ActionContext<EventState, EventState>,
    { eventID, classID }: { eventID: number; classID: number }
  ): Promise<void> {
    try {
      const response: EventRequest = await InterfaceEventUseCases.getEventCourse(
        eventID
      );
      commit("SET_EVENT_REQUEST", response);
      commit("SET_EVENT", response.event);

      const contents = await eventContentUseCase.getAllContents(
        eventID,
        classID
      );
      commit("SET_EVENT_CONTENTS", contents);
    } catch (e) {
      console.log(e);
    }
  },

  getClass: (ctx: ActionContext<EventState, EventState>, id: number) => {
    const eventClass =
      ctx.getters.event?.classes?.filter(
        eventClass => eventClass.id === id
      )[0] || new EventClass();
    ctx.commit("SET_EVENT_CLASS", eventClass);
  },

  async loadUserEnrollment(ctx: ActionContext<EventState, EventState>) {
    const user: User = await store.getters["userProfile_module/user"];
    const enrolledUsers: User[] = ctx.getters.eventClass.enrollUsers;
    ctx.commit("UN_ENROLL_USER");
    ctx.commit("UN_ATTENDANCE_USER");

    if (!enrolledUsers) return;

    const userInEnrroledList = enrolledUsers.find(
      userEnrolled => userEnrolled.id === user.id
    );

    const dateQrCode = state.dateQrCode;

    if (userInEnrroledList) {
      ctx.commit("ENROLL_USER", userInEnrroledList);

      if (dateQrCode) {
        const userAttendanceAt = userInEnrroledList.attendanceAts?.filter(
          (attendance: AttendanceAt) => {
            return dateGrouped(attendance.trainingDate) === dateQrCode;
          }
        );

        if (userAttendanceAt && userAttendanceAt.length) {
          ctx.commit("ATTENDANCE_USER");
        }
      } else if (
        userInEnrroledList.attendanceAts &&
        userInEnrroledList.attendanceAts?.length
      )
        ctx.commit("ATTENDANCE_USER");
    }
  },

  async enroll(
    { commit }: ActionContext<EventState, EventState>,
    { user, eventClass }: { user: User; eventClass: EventClass }
  ): Promise<void> {
    await InterfaceEventUseCases.enrollUser(user, eventClass);
    if (!eventClass.enrollUsers) {
      eventClass.enrollUsers = [];
    }
    eventClass.enrollUsers.push(user);
    commit("SET_EVENT_CLASS", eventClass);
    commit("ENROLL_USER");
  },

  async unEnrollUser(
    { commit }: ActionContext<EventState, EventState>,
    { user, eventClass }: { user: User; eventClass: EventClass }
  ): Promise<void> {
    await InterfaceEventUseCases.unEnrollUser(user, eventClass);
    if (!eventClass.enrollUsers) {
      eventClass.enrollUsers = [];
    }
    eventClass.enrollUsers = eventClass.enrollUsers.filter(
      enrolledUser => enrolledUser.id !== user.id
    );
    commit("SET_EVENT_CLASS", eventClass);
    commit("UN_ENROLL_USER");
    commit("UN_ATTENDANCE_USER");
  },

  async confirmCheckinQrCode(
    { commit }: ActionContext<EventState, EventState>,
    payload: { token: string; date: string }
  ): Promise<void> {
    return await InterfaceEventUseCases.confirmCheckinQrCode(
      payload.token,
      payload.date
    );
  },

  async getInstructors(
    { commit }: ActionContext<EventState, EventState>,
    payload: { eventID: number; classID: number }
  ): Promise<void> {
    const response = await InterfaceEventUseCases.getInstructors(
      payload.eventID,
      payload.classID
    );

    commit("SET_INSTRUCTORS", response);
  },

  async generateCertificate(
    { commit }: ActionContext<EventState, EventState>,
    payload: { eventID: number; classID: number }
  ): Promise<void> {
    const response = await InterfaceEventUseCases.generateCertificate(
      payload.eventID,
      payload.classID
    );

    commit("SET_USER_CERTIFICATE", response);
  },

  async setDateQrCode(
    { commit }: ActionContext<EventState, EventState>,
    date: string
  ): Promise<void> {
    commit("SET_DATE_QR_CODE", date);
  }
};

const getters = {
  eventsBasedOnClass: (state: EventState): EventBasedOnClass[] =>
    state.eventsBasedOnClass || [],
  event: (state: EventState): EventCourse => state.event || new EventCourse(),
  eventCourses: (state: EventState): EventCourse[] => state.eventCourses || [],
  contents: (state: EventState): Content[] => state.contents || [],
  eventClass: (state: EventState): EventClass =>
    state.eventClass || new EventClass(),
  isUserEnrolled: (state: EventState): boolean => state.isUserEnrolled,
  isUserAttendance: (state: EventState): boolean => state.isUserAttendance,
  instructors: (state: EventState): Instructor[] => state.instructors,
  certificate: (state: EventState): string => state.certificate,
  enrolledUser: (state: EventState): User => state.enrolledUser,
  dateQrCode: (state: EventState): string => state.dateQrCode
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
